import { type FC } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { renderSampleAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useEventNotificationGridUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import type { InputTableQuerySpec, Sample } from "@/graphql.ts";
import {
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  type ITableData,
  type ITableDataState,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, EVENT_TABLE_COLUMNS } from "@/settings/settings";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import { AttributesListWidget } from "@/ui-modules/core/navigation-panel/AttributeWidget.tsx";
import { SampleLockedIcon } from "@/ui-modules/core/tiles/components/SampleTile.tsx";

import styleLayout from "../../core/styles/PageLayout.module.scss";
import GenericEntitySummaryCardView from "../GenericEntitySummaryView.js";

interface SampleEventsViewProps {
  sampleId: number;
  sample: Sample;
}
/**
 * Either sampleId or parentId, both at the same time is prohibited
 */
export const SampleEventsView: FC<SampleEventsViewProps> = (props) => {
  const eventsTableSpec: InputTableQuerySpec = {
    resultType: ENTITY_TYPE.EVENT,
    id: props.sampleId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.SAMPLE,
  };

  const tableData: ITableData = useSelector<IAppState, ITableData>(
    (state) => getTableDataSlice(state.tableData, eventsTableSpec),
    shallowEqual,
  );

  const eventsTableInitOptions = {
    sortExpression: `-${EVENT_TABLE_COLUMNS.CREATED}`,
  };

  // create count label
  const getCount = () => {
    if (tableData?.totalRowCount) {
      if (tableData.filteredRowCount === tableData.totalRowCount) {
        return String(tableData.totalRowCount);
      } else if (tableData.filteredRowCount < tableData.totalRowCount) {
        return `${tableData.filteredRowCount} of ${tableData.totalRowCount}`;
      }
    }
    return undefined;
  };

  useEventNotificationGridUpdate(eventsTableSpec);

  return (
    <>
      <GenericEntitySummaryCardView
        title={props.sample?.name ?? ""}
        subtitle={props.sample?.description ?? "Playbook Events"}
        titleTrailer={props.sample?.locked ? <SampleLockedIcon size={20} /> : null}
        entity={{ id: props.sample && props.sample.id, groupType: ENTITY_TYPE.SAMPLE }}
        project={props.sample?.project}
        setDocumentTitle
        badge={getCount()}
        headerAttributes={[
          <AttributesListWidget
            attributes={renderSampleAttributesWidgets(props.sample)}
            key={"sample-data-view-widget-1"}
          />,
        ]}
        tableQuerySpec={eventsTableSpec}
        tableDataState={tableData as unknown as ITableDataState}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <DataTableContainer
          key={createUniqueKeyFromTableSpec(eventsTableSpec)}
          tableSpec={eventsTableSpec}
          hideCheckboxes={true}
          tableInitializationOptions={eventsTableInitOptions}
          tableData={tableData}
          doNotShowPerfGuidance={false}
          wrapColumnHeaders={false}
          sequenceFeatureTypes={{
            aaFeatureTypes: null,
            markerTypes: null,
            nuFeatureTypes: null,
          }}
        />
      </div>
    </>
  );
};
